<!-- eslint-disable vue/no-template-shadow -->
<template>
  <div class="spontaneous-conversation-test p-test-page">
    <!-- Pineapple Header -->
    <a-page-head title="AI 자율대화 테스트" :breadcrumbs="[{ label: '테스트' }, { label: 'AI 자율대화' }]">
      <template #actions>
        <a-btn v-if="messages.length > 0" kind="ghost" size="md" @click="resetConversation">
          <template #icon>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="1 4 1 10 7 10"/>
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
            </svg>
          </template>
          초기화
        </a-btn>
      </template>
    </a-page-head>

    <!-- Configuration Card -->
    <div class="card mb-4" v-if="!conversationStarted">
      <div class="card-header bg-gradient-primary text-white">
        <h5 class="mb-0 text-white">
          <i class="material-icons me-2" style="vertical-align: middle; font-size: 20px;">settings</i>
          대화 설정
        </h5>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="form-label fw-bold">교재 바코드 (Barcode)</label>
            <input
              v-model="config.barcode"
              type="text"
              class="form-control"
              placeholder="9781635190151"
            />
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label fw-bold">챕터 번호 (Chapter)</label>
            <input
              v-model.number="config.chapterNumber"
              type="number"
              class="form-control"
              placeholder="1"
            />
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label fw-bold">고유키 (Unique Key)</label>
            <input
              v-model="config.uniqueKey"
              type="text"
              class="form-control"
              placeholder=""
            />
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label fw-bold">사용자 ID (userIdx)</label>
            <input
              v-model.number="userIdx"
              type="number"
              class="form-control"
              min="1"
              placeholder="1"
            />
          </div>
          <div class="col-md-12 mb-3">
            <label class="form-label fw-bold">세션 ID</label>
            <input
              v-model="config.sessionId"
              type="text"
              class="form-control"
              placeholder="session_2024_001"
            />
          </div>
          <div class="col-12">
            <button
              @click="startConversation"
              class="btn btn-primary w-100"
              :disabled="!canStart"
            >
              <i class="material-icons me-2" style="vertical-align: middle; font-size: 18px;">play_arrow</i>
              대화 시작하기
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Chat Interface -->
    <div class="card chat-container" v-if="conversationStarted">
      <div class="card-header bg-gradient-success text-white">
        <div class="d-flex justify-content-between align-items-center">
          <h5 class="mb-0 text-white">
            <i class="material-icons me-2" style="vertical-align: middle; font-size: 20px;">forum</i>
            {{ conversationBlocked ? '대화 종료됨' : '대화 진행 중' }}
          </h5>
          <div class="d-flex gap-2">
            <div class="badge bg-white text-success">
              대화 턴: {{ conversationTurnCount }}{{ requestParamsJson ? '/' + maxConversationTurns : '' }}
            </div>
            <div
              v-if="requestParamsJson"
              class="badge"
              :class="abusingCount > 0 ? (abusingCount >= abusingLimit ? 'bg-danger' : 'bg-warning') : 'bg-secondary'"
            >
              {{ abusingCount > 0 ? '⚠️ ' : '' }}부적절 응답: {{ abusingCount }}/{{ abusingLimit }}
            </div>
          </div>
        </div>
      </div>

      <!-- Messages Area -->
      <div class="card-body chat-messages" ref="messagesContainer">
        <div
          v-for="(message, index) in messages"
          :key="index"
          :class="['message-wrapper', message.role === 'user' ? 'user-message' : 'ai-message']"
        >
          <div class="message-bubble">
            <div class="message-header">
              <span class="message-role">
                <i class="material-icons">{{ message.role === 'user' ? 'person' : 'smart_toy' }}</i>
                {{ message.role === 'user' ? '사용자' : 'AI 튜터' }}
              </span>
              <span class="message-time">{{ message.timestamp }}</span>
            </div>
            <div class="message-content">{{ message.content }}</div>
            <div v-if="message.evaluation" class="message-evaluation">
              <div class="evaluation-header">
                <i class="material-icons">assessment</i>
                평가 결과
              </div>
              <div class="evaluation-details">
                <div v-if="message.evaluation.rubric_scores" class="mb-2">
                  <strong>루브릭 점수:</strong>
                  <div v-for="(score, idx) in message.evaluation.rubric_scores" :key="idx" class="score-item">
                    <span class="badge bg-primary me-2">{{ score.criteria }}</span>
                    <span class="badge bg-success">{{ score.score }}점</span>
                  </div>
                </div>
                <div v-if="message.evaluation.checklist_results">
                  <strong>체크리스트:</strong>
                  <div v-for="(item, idx) in message.evaluation.checklist_results" :key="idx" class="checklist-item">
                    <i :class="['material-icons', item.completed ? 'text-success' : 'text-muted']">
                      {{ item.completed ? 'check_circle' : 'radio_button_unchecked' }}
                    </i>
                    {{ item.name }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 실시간 타이핑 중인 AI 메시지 (SSE partial response) -->
        <div v-if="currentAiMessage" class="message-wrapper ai-message">
          <div class="message-bubble typing-message">
            <div class="message-header">
              <span class="message-role">
                <i class="material-icons">smart_toy</i>
                AI 튜터 (입력 중...)
              </span>
            </div>
            <div class="message-content" style="opacity: 0.9;">
              {{ currentAiMessage }}
              <span class="typing-cursor">|</span>
            </div>
          </div>
        </div>

        <!-- Loading Indicator (연결 대기 중) -->
        <div v-if="isLoading && !currentAiMessage" class="message-wrapper ai-message">
          <div class="message-bubble">
            <div class="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="messages.length === 0 && !isLoading" class="empty-state">
          <i class="material-icons">chat_bubble_outline</i>
          <p>아래 입력창에 메시지를 입력하여 대화를 시작하세요</p>
        </div>
      </div>

      <!-- Input Area -->
      <div class="card-footer chat-input-area">
        <div v-if="conversationBlocked" class="alert alert-danger mb-0">
          <i class="material-icons me-2" style="vertical-align: middle;">block</i>
          부적절한 응답이 {{ abusingLimit }}회 감지되어 대화가 종료되었습니다. 대화를 초기화하여 다시 시작하세요.
        </div>
        <div v-else class="input-group">
          <input
            v-model="userInput"
            type="text"
            class="form-control"
            placeholder="메시지를 입력하세요..."
            @keyup.enter="sendMessage"
            :disabled="isLoading"
          />
          <button
            @click="sendMessage"
            class="btn btn-primary"
            :disabled="!userInput.trim() || isLoading"
          >
            <i class="material-icons">send</i>
          </button>
        </div>
      </div>
    </div>

    <!-- API Request/Response Log Card (개발용) -->
    <div class="card mt-4" v-if="apiLogs.length > 0 && conversationStarted">
      <div class="card-header bg-gradient-info text-white">
        <div class="d-flex justify-content-between align-items-center">
          <h6 class="mb-0 text-white">
            <i class="material-icons me-2" style="vertical-align: middle; font-size: 18px;">code</i>
            API 요청/응답 로그 ({{ apiLogs.length }}건)
          </h6>
          <button @click="clearLogs" class="btn btn-sm btn-white">
            <i class="material-icons" style="font-size: 14px;">delete</i>
            로그 삭제
          </button>
        </div>
      </div>
      <div class="card-body p-0">
        <div class="api-logs-container">
          <div
            v-for="(log, index) in apiLogs"
            :key="index"
            class="log-entry"
            :class="{ 'log-error': log.error }"
          >
            <div class="log-header" @click="toggleLog(index)">
              <div class="log-info">
                <span :class="['log-badge', log.error ? 'bg-danger' : 'bg-success']">
                  {{ log.error ? '❌' : '✅' }} #{{ apiLogs.length - index }}
                </span>
                <span class="log-time">{{ log.timestamp }}</span>
                <span class="log-message">{{ log.userMessage }}</span>
              </div>
              <i class="material-icons">
                {{ log.expanded ? 'expand_less' : 'expand_more' }}
              </i>
            </div>
            <div v-if="log.expanded" class="log-details">
              <!-- 요청 정보 -->
              <div class="log-section">
                <h6 class="log-section-title">
                  <i class="material-icons">upload</i>
                  요청 (Request)
                </h6>
                <pre class="debug-response">{{ JSON.stringify(log.request, null, 2) }}</pre>
              </div>
              <!-- 응답 정보 -->
              <div class="log-section">
                <h6 class="log-section-title">
                  <i class="material-icons">download</i>
                  응답 (Response)
                </h6>
                <pre class="debug-response">{{ JSON.stringify(log.response, null, 2) }}</pre>
              </div>
              <!-- 에러 정보 -->
              <div v-if="log.error" class="log-section">
                <h6 class="log-section-title text-danger">
                  <i class="material-icons">error</i>
                  에러 상세
                </h6>
                <pre class="debug-response">{{ JSON.stringify(log.errorDetails, null, 2) }}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import APageHead from '@/components/Pineapple/APageHead.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';

export default {
  name: "SpontaneousSpeakingTest",
  components: { APageHead, ABtn },
  data() {
    return {
      // 대화 설정
      config: {
        barcode: '9781635190151',
        chapterNumber: 1,
        uniqueKey: '',
        promptKind: 'spontaneous_conversation',
        sessionId: `session_${Date.now()}`
      },

      // 대화 상태
      conversationStarted: false,
      messages: [],
      userInput: '',
      isLoading: false,
      isTestingConnection: false,
      apiLogs: [],

      // API 성능 최적화 (requestParamsJson 캐싱)
      requestParamsJson: null,

      // 대화 제한 (백엔드에서 받은 값)
      abusingLimit: 3,  // 기본값, 백엔드에서 업데이트됨
      maxConversationTurns: 10,  // 기본값, 백엔드에서 업데이트됨

      // 어뷰징 체크 및 대화 턴 카운트
      abusingCount: 0,
      conversationTurnCount: 0,
      conversationBlocked: false,

      // 사용자 식별자 (실제 환경에서는 인증된 사용자 정보에서 가져와야 함)
      userIdx: 1,

      // 실시간 타이핑 효과용 임시 메시지
      currentAiMessage: ''
    };
  },

  computed: {
    canStart() {
      return this.config.barcode.trim() &&
             this.config.chapterNumber > 0 &&
             this.config.uniqueKey.trim() &&
             this.config.sessionId.trim();
    }
  },

  // 컴포넌트 언마운트 시 정리
  beforeUnmount() {
    // SSE 스트리밍은 fetch API로 관리되므로 별도 정리 불필요
    console.log('🔌 컴포넌트 언마운트');
  },

  methods: {
    // 대화 시작
    startConversation() {
      // 설정이 유효한지 확인
      if (!this.canStart) {
        alert('모든 필수 정보를 입력해주세요.');
        return;
      }

      // 대화 시작 (프롬프트는 첫 API 호출 시 자동 생성됨)
      this.conversationStarted = true;
      console.log('대화 시작:', {
        barcode: this.config.barcode,
        chapterNumber: this.config.chapterNumber,
        uniqueKey: this.config.uniqueKey,
        sessionId: this.config.sessionId
      });
    },

    // 메시지 전송
    async sendMessage() {
      if (!this.userInput.trim() || this.isLoading) return;

      // 어뷰징으로 인한 대화 차단 체크
      if (this.conversationBlocked) {
        alert('부적절한 언어 사용이 3회 이상 감지되어 대화가 차단되었습니다.');
        return;
      }

      const userMessage = this.userInput.trim();
      this.userInput = '';

      // API 호출 (메시지 추가 전에 먼저 호출)
      await this.callConversationAPI(userMessage);
    },

    // API 호출 (POST + SSE 스트리밍) - 백엔드 POST 마이그레이션 대응
    async callConversationAPI(userMessage) {
      this.isLoading = true;

      // 사용자 메시지를 먼저 화면에 표시
      this.addMessage('user', userMessage);

      const logEntry = {
        timestamp: this.formatFullTime(new Date()),
        userMessage: userMessage,
        request: null,
        response: null,
        error: false,
        errorDetails: null,
        expanded: false
      };

      try {
        // dialogueHistory 생성 (방금 추가한 사용자 메시지 제외)
        const dialogueHistory = this.messages
          .slice(0, -1)  // 마지막 메시지(방금 추가한 사용자 메시지) 제외
          .filter(m => m.role === 'user' || m.role === 'ai')
          .map(m => ({
            role: m.role,
            message: m.content
          }));

        // 백엔드 엔드포인트 URL (POST 방식)
        const apiUrl = `${this.$CONF.API_URL}/ai-prompt/spontaneous-conversation-sse/${this.userIdx}/${this.config.barcode}/${this.config.chapterNumber}/${this.config.uniqueKey}`;

        // Request Body 구성 (POST body)
        const requestBody = {
          dialogueHistory: dialogueHistory,
          userMessage: userMessage,
          requestParamsJson: this.requestParamsJson  // null이면 첫 요청, 있으면 재사용
        };

        // 로그에 요청 정보 저장
        logEntry.request = {
          url: apiUrl,
          method: 'POST',
          body: requestBody
        };

        console.log('========================================');
        console.log('SSE Request (POST + fetch + ReadableStream):', {
          url: apiUrl,
          body: requestBody
        });
        console.log('========================================');

        // 인증 토큰 가져오기 (localStorage에서)
        const token = localStorage.getItem('access_token');
        if (!token || token === 'null') {
          throw new Error('인증 토큰이 없습니다. 다시 로그인해주세요.');
        }

        // fetch + ReadableStream 방식으로 SSE 처리 (POST + Authorization 헤더 포함)
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        console.log('✅ 연결 성공, 스트리밍 시작...');

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let data = null;
        let streamDone = false;

        // 라인 파싱 헬퍼 함수
        const parseLine = (trimmedLine) => {
          let eventData = null;

          try {
            if (trimmedLine.startsWith('data: ')) {
              // 표준 SSE 형식
              const jsonStr = trimmedLine.substring(6);
              eventData = JSON.parse(jsonStr);
            } else if (trimmedLine.startsWith('{') || trimmedLine.startsWith('[')) {
              // 직접 JSON 형식
              eventData = JSON.parse(trimmedLine);
            } else {
              // 기타 형식 (예: "response:{...}")
              const colonIndex = trimmedLine.indexOf(':');
              if (colonIndex > 0 && colonIndex < trimmedLine.length - 1) {
                const jsonStr = trimmedLine.substring(colonIndex + 1);
                eventData = JSON.parse(jsonStr);
              }
            }

            if (eventData) {
              console.log('✅ 파싱 성공:', eventData);

              // request_params_json이 포함된 경우 즉시 캐싱
              if (eventData.request_params_json) {
                this.requestParamsJson = eventData.request_params_json;
                console.log('💾 requestParamsJson 캐싱됨');
              }

              // 실시간 타이핑 효과 (AI 메시지가 있는 경우)
              const outputData = eventData.results?.outputData || eventData;
              if (outputData.aiMessage) {
                this.currentAiMessage = outputData.aiMessage;

                // 스크롤을 최신 메시지로 이동
                this.$nextTick(() => {
                  this.scrollToBottom();
                });
              }
            }
          } catch (e) {
            console.warn('⚠️ SSE 데이터 파싱 실패:', trimmedLine, e);
          }

          return eventData;
        };

        // SSE 스트리밍 처리
        while (!streamDone) {
          const { done, value } = await reader.read();

          if (done) {
            console.log('📭 스트리밍 종료');
            streamDone = true;
            break;
          }

          // 청크 디코딩
          buffer += decoder.decode(value, { stream: true });

          // 줄바꿈 단위로 처리
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // 마지막 불완전한 줄은 buffer에 남김

          for (const line of lines) {
            const trimmedLine = line.trim();
            if (trimmedLine === '') continue;

            console.log('📥 SSE Line:', line);

            const parsedData = parseLine(trimmedLine);
            if (parsedData) {
              data = parsedData; // 최종 데이터 저장 (마지막 이벤트가 최종 응답)
            }
          }
        }

        // 스트리밍 종료 후 남은 버퍼 처리
        if (buffer.trim()) {
          console.log('📦 남은 버퍼 처리:', buffer);
          const parsedData = parseLine(buffer.trim());
          if (parsedData) {
            data = parsedData;
          }
        }

        // 최종 데이터 검증
        if (!data) {
          throw new Error('SSE 스트리밍에서 데이터를 받지 못했습니다.');
        }

        console.log('📦 최종 데이터:', data);

        // results.outputData에서 실제 데이터 추출
        const outputData = data.results?.outputData || data;
        const aiMessage = outputData.aiMessage;
        console.log('💬 AI Message:', aiMessage);

        // AI 메시지 추가
        if (aiMessage) {
          this.addMessage('ai', aiMessage);
          this.currentAiMessage = '';
        } else {
          throw new Error('AI 응답 메시지를 받지 못했습니다.');
        }

        // 다음 요청을 위해 request_params_json 저장 및 limitInfo 추출
        if (data.request_params_json) {
          this.requestParamsJson = data.request_params_json;
          console.log('💾 requestParamsJson 저장됨');

          // request_params_json에서 limitInfo 추출
          try {
            const requestParams = JSON.parse(data.request_params_json);
            if (requestParams.inputData?.limitInfo) {
              this.abusingLimit = requestParams.inputData.limitInfo.abusingLimit || 3;
              this.maxConversationTurns = requestParams.inputData.limitInfo.maxConversationTurns || 10;
              console.log('📊 대화 제한 설정:', {
                abusingLimit: this.abusingLimit,
                maxConversationTurns: this.maxConversationTurns
              });
            }
          } catch (parseError) {
            console.warn('⚠️ limitInfo 파싱 실패:', parseError);
          }
        }

        // 대화 턴 카운트 증가
        this.conversationTurnCount++;
        console.log(`📊 대화 턴: ${this.conversationTurnCount}/${this.maxConversationTurns}`);

        // 학습 범위 검증
        const scopeCheckResult = outputData.scopeCheckResult;
        if (scopeCheckResult?.isOutOfScope) {
          console.warn('⚠️ 학습 범위 벗어남:', scopeCheckResult.reason);
          //alert(`학습 범위를 벗어났습니다:\n\n${JSON.stringify(scopeCheckResult.reason, null, 2)}`);
        }

        // 부적절한 언어 및 최대 턴 도달 통합 처리 (중복 호출 방지)
        const abusingCheckResult = outputData.abusingCheckResult;
        const isAbusing = !!(abusingCheckResult && abusingCheckResult.isAbusing);
        if (isAbusing) {
          this.abusingCount++;
          console.error(`🚨 부적절한 응답 감지 (${this.abusingCount}/${this.abusingLimit}):`, abusingCheckResult.reason);
        }

        let blockedReason = null;
        if (this.conversationTurnCount >= this.maxConversationTurns) {
          blockedReason = 'max_turns_reached';
        }

        if (blockedReason) {
          this.conversationBlocked = true;
          if (blockedReason === 'max_turns_reached') {
            alert(`✅ 대화가 완료되었습니다\n\n최대 대화 횟수(${this.maxConversationTurns}회)에 도달했습니다.`);
          }
          await this.callEvaluationAPI(blockedReason);
        }

        // 로그에 응답 정보 저장
        logEntry.response = data;
        this.isLoading = false;

      } catch (error) {
        // 에러 로그 저장
        logEntry.error = true;
        logEntry.errorDetails = {
          message: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString()
        };
        logEntry.response = { error: error.message };

        console.error('❌ SSE API 호출 오류:', error);

        // 에러 타입별 사용자 친화적 메시지
        let errorMessage = '오류가 발생했습니다.';

        if (error.message.includes('인증')) {
          errorMessage = '인증 토큰이 만료되었거나 유효하지 않습니다. 다시 로그인해주세요.';
        } else if (error.message.includes('HTTP error')) {
          const statusMatch = error.message.match(/status: (\d+)/);
          const status = statusMatch ? statusMatch[1] : 'unknown';

          if (status === '400') {
            errorMessage = '잘못된 요청입니다. 입력 데이터를 확인해주세요.';
          } else if (status === '401') {
            errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
          } else if (status === '403') {
            errorMessage = '접근 권한이 없습니다.';
          } else if (status === '404') {
            errorMessage = 'API 엔드포인트를 찾을 수 없습니다.';
          } else if (status === '500') {
            errorMessage = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
          } else {
            errorMessage = `서버 오류 (${status}): 잠시 후 다시 시도해주세요.`;
          }
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          errorMessage = '네트워크 연결을 확인해주세요.';
        } else if (error.message.includes('timeout')) {
          errorMessage = '요청 시간이 초과되었습니다. 다시 시도해주세요.';
        } else if (error.message.includes('SSE')) {
          errorMessage = 'SSE 스트리밍 처리 중 오류가 발생했습니다.';
        }

        this.addMessage('ai', `${errorMessage}\n\n상세: ${error.message}`);
        this.currentAiMessage = '';  // 타이핑 메시지 초기화
      } finally {
        // 로그 추가 (최신 로그가 맨 앞에 오도록)
        this.apiLogs.unshift(logEntry);
        this.isLoading = false;
      }
    },

    // 대화 평가 API 호출
    async callEvaluationAPI(reason) {
      console.log('📊 대화 평가 API 호출 시작:', reason);

      try {
        // dialogueHistory 생성 (전체 대화 내역)
        const dialogueHistory = this.messages
          .filter(m => m.role === 'user' || m.role === 'ai')
          .map(m => ({
            role: m.role,
            message: m.content
          }));

        // 실제 평가 API 엔드포인트
        const evaluationUrl = `/ai-prompt/evaluation-spontaneous-conversation/${this.userIdx}/${this.config.barcode}/${this.config.chapterNumber}/${this.config.uniqueKey}`;

        // 마지막 사용자 메시지 찾기
        const lastUserMessage = this.messages
          .filter(m => m.role === 'user')
          .pop()?.content || '';

        // 쿼리 파라미터 준비
        const queryParams = new URLSearchParams({
          requestParamsJson: this.requestParamsJson,
          dialogueHistory: JSON.stringify(dialogueHistory),
          userMessage: lastUserMessage,  // 마지막 사용자 메시지 추가
          abusingCount: this.abusingCount || 0
        });

        console.log('📤 평가 요청 URL:', evaluationUrl);
        console.log('📤 평가 요청 쿼리 파라미터:', {
          requestParamsJson: this.requestParamsJson,
          dialogueHistory: dialogueHistory,
          userMessage: lastUserMessage,
          abusingCount: this.abusingCount || 0
        });

        const response = await this.$axios({
          method: 'post',
          url: `${evaluationUrl}?${queryParams.toString()}`,
          loadingBar: true
        });

        console.log('📥 평가 응답:', response.data);

        // 평가 결과 표시
        if (response.data.success) {
          const evaluation = response.data.evaluation_result;
          if (evaluation) {
            let resultMessage = '대화 평가가 완료되었습니다!\n\n';

            // 평가 결과가 있을 경우 표시
            if (evaluation.overall_score !== undefined) {
              resultMessage += `종합 점수: ${evaluation.overall_score}\n`;
            }
            if (evaluation.feedback) {
              resultMessage += `피드백: ${evaluation.feedback}\n`;
            }

            // MongoDB 저장 정보 표시 (개발용)
            if (response.data.document_id) {
              console.log('📄 MongoDB Document ID:', response.data.document_id);
            }
            if (response.data.request_id) {
              console.log('🔍 Genesis Lab Request ID:', response.data.request_id);
            }

            alert(resultMessage);
          } else {
            alert('평가가 완료되었지만 결과를 표시할 수 없습니다.');
          }
        } else {
          alert(response.data.message || '평가 처리 중 문제가 발생했습니다.');
        }

      } catch (error) {
        console.error('❌ 평가 API 호출 오류:', error);
        alert('대화 평가 중 오류가 발생했습니다.');
      }
    },

    // 메시지 추가
    addMessage(role, content, evaluation = null) {
      const message = {
        role,
        content,
        timestamp: this.formatTime(new Date()),
        evaluation
      };

      this.messages.push(message);

      // 스크롤을 최신 메시지로 이동
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },

    // 시간 포맷 (간단)
    formatTime(date) {
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    },

    // 시간 포맷 (상세)
    formatFullTime(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },

    // 스크롤 하단으로 이동
    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },

    // 대화 초기화
    resetConversation() {
      if (confirm('대화를 초기화하시겠습니까?')) {
        this.conversationStarted = false;
        this.messages = [];
        this.userInput = '';
        this.apiLogs = [];
        this.requestParamsJson = null;  // 캐시된 requestParamsJson 초기화
        this.abusingCount = 0;  // 어뷰징 카운터 초기화
        this.conversationTurnCount = 0;  // 대화 턴 카운터 초기화
        this.conversationBlocked = false;  // 대화 차단 해제
        this.currentAiMessage = '';  // 타이핑 메시지 초기화
        this.abusingLimit = 3;  // 기본값으로 리셋
        this.maxConversationTurns = 10;  // 기본값으로 리셋
        this.config.sessionId = `session_${Date.now()}`;
      }
    },

    // 로그 토글
    toggleLog(index) {
      this.apiLogs[index].expanded = !this.apiLogs[index].expanded;
    },

    // 로그 삭제
    clearLogs() {
      if (confirm('모든 API 로그를 삭제하시겠습니까?')) {
        this.apiLogs = [];
      }
    }
  }
};
</script>

<style scoped>
/* Container */
.spontaneous-conversation-test {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

/* Gradient backgrounds */
.bg-gradient-dark {
  background: linear-gradient(135deg, #434343 0%, #000000 100%);
  color: white;
}

.bg-gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.bg-gradient-success {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
}

.bg-gradient-info {
  background: linear-gradient(135deg, #36d1dc 0%, #5b86e5 100%);
}

.opacity-8 {
  opacity: 0.8;
}

/* Cards */
.card {
  border: none;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  border-radius: 10px;
  overflow: hidden;
}

.card:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

/* Form Inputs */
.form-control,
.form-select {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 10px 15px;
  font-size: 14px;
  transition: all 0.3s ease;
}

.form-control:focus,
.form-select:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
  outline: none;
}

.form-control::placeholder {
  color: #adb5bd;
}

.form-label {
  color: #344767;
  font-size: 13px;
  margin-bottom: 8px;
}

/* Chat Container */
.chat-container {
  height: 600px;
  display: flex;
  flex-direction: column;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f8f9fa;
}

.chat-input-area {
  border-top: 1px solid #e9ecef;
  padding: 15px;
  background: white;
}

/* Messages */
.message-wrapper {
  display: flex;
  margin-bottom: 15px;
  animation: fadeIn 0.3s ease;
}

.user-message {
  justify-content: flex-end;
}

.ai-message {
  justify-content: flex-start;
}

.message-bubble {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.user-message .message-bubble {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-bottom-right-radius: 4px;
}

.ai-message .message-bubble {
  background: white;
  border-bottom-left-radius: 4px;
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 12px;
  opacity: 0.8;
}

.message-role {
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
}

.message-role .material-icons {
  font-size: 16px;
}

.message-time {
  font-size: 11px;
}

.message-content {
  line-height: 1.5;
  word-wrap: break-word;
}

.ai-message .message-content {
  color: #344767;
}

/* Message Evaluation */
.message-evaluation {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.user-message .message-evaluation {
  border-top-color: rgba(255, 255, 255, 0.3);
}

.evaluation-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 13px;
}

.evaluation-header .material-icons {
  font-size: 16px;
}

.evaluation-details {
  font-size: 12px;
}

.score-item,
.checklist-item {
  margin: 4px 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.checklist-item .material-icons {
  font-size: 18px;
}

/* Typing Indicator */
.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 8px 0;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #667eea;
  animation: typing 1.4s infinite;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  30% {
    transform: translateY(-10px);
    opacity: 1;
  }
}

/* Typing Cursor (SSE 실시간 타이핑 효과) */
.typing-message {
  background: linear-gradient(135deg, #f0f4ff 0%, #e8f0ff 100%) !important;
  border-left: 3px solid #667eea !important;
}

.typing-cursor {
  display: inline-block;
  margin-left: 2px;
  font-weight: bold;
  color: #667eea;
  animation: blink 1s infinite;
}

@keyframes blink {
  0%, 50% {
    opacity: 1;
  }
  51%, 100% {
    opacity: 0;
  }
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #adb5bd;
}

.empty-state .material-icons {
  font-size: 64px;
  margin-bottom: 16px;
}

/* Input Group */
.input-group {
  display: flex;
  gap: 8px;
}

.input-group .form-control {
  flex: 1;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  padding: 10px 15px;
}

.input-group .form-control:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.input-group .btn {
  border-radius: 8px;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.input-group .btn .material-icons {
  font-size: 20px;
}

/* Buttons */
.btn-white {
  background: white;
  color: #344767;
  border: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-white:hover {
  background: #f8f9fa;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn-info {
  background: linear-gradient(135deg, #36d1dc 0%, #5b86e5 100%);
  color: white;
  border: none;
  box-shadow: 0 2px 4px rgba(54, 209, 220, 0.3);
}

.btn-info:hover:not(:disabled) {
  background: linear-gradient(135deg, #5b86e5 0%, #36d1dc 100%);
  box-shadow: 0 4px 8px rgba(54, 209, 220, 0.4);
}

.btn-info:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-info .material-icons {
  animation: none;
}

.btn-info:disabled .material-icons {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* API Logs */
.api-logs-container {
  max-height: 600px;
  overflow-y: auto;
}

.log-entry {
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s ease;
}

.log-entry:hover {
  background-color: #f8f9fa;
}

.log-entry.log-error {
  background-color: #fff5f5;
}

.log-entry.log-error:hover {
  background-color: #ffe9e9;
}

.log-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  cursor: pointer;
  user-select: none;
}

.log-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.log-badge {
  padding: 4px 10px;
  border-radius: 12px;
  color: white;
  font-size: 12px;
  font-weight: 600;
}

.log-time {
  font-size: 12px;
  color: #6c757d;
  font-family: monospace;
}

.log-message {
  font-size: 13px;
  color: #344767;
  font-weight: 500;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.log-header .material-icons {
  color: #6c757d;
  transition: transform 0.2s ease;
}

.log-details {
  padding: 0 20px 20px 20px;
  animation: fadeIn 0.3s ease;
}

.log-section {
  margin-bottom: 15px;
}

.log-section:last-child {
  margin-bottom: 0;
}

.log-section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #344767;
  margin-bottom: 8px;
}

.log-section-title .material-icons {
  font-size: 16px;
}

.debug-response {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  font-size: 11px;
  max-height: 300px;
  overflow-y: auto;
  margin: 0;
  font-family: 'Courier New', monospace;
  line-height: 1.4;
  border: 1px solid #e9ecef;
}

.api-logs-container::-webkit-scrollbar,
.debug-response::-webkit-scrollbar {
  width: 6px;
}

.api-logs-container::-webkit-scrollbar-track,
.debug-response::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.api-logs-container::-webkit-scrollbar-thumb,
.debug-response::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 3px;
}

.api-logs-container::-webkit-scrollbar-thumb:hover,
.debug-response::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Scrollbar */
.chat-messages::-webkit-scrollbar {
  width: 8px;
}

.chat-messages::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}

/* Responsive */
@media (max-width: 768px) {
  .spontaneous-conversation-test {
    padding: 10px;
  }

  .message-bubble {
    max-width: 85%;
  }

  .chat-container {
    height: 500px;
  }
}
</style>
